// src/Hooks/useImportExcelDocuments.js

import { useState } from 'react';

const normalizeString = (str) => {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/[\s,]+/g, '')
    .toLowerCase();
};

export const useImportExcelDocuments = (setParsedData, rowMappings, customRows) => {
  const [documentMatchRowId, setDocumentMatchRowId] = useState(null);
  const [stagedDocumentFiles, setStagedDocumentFiles] = useState([]);
  const [localSelectedDocs, setLocalSelectedDocs] = useState([]);

  const handleDocumentFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // AMENDED: Generate Object URLs to pin the files in memory before clearing the input,
      // preventing the OS from destroying the blob before Firebase can upload it.
      files.forEach((f) => {
        if (!f.preview) f.preview = URL.createObjectURL(f);
      });

      setStagedDocumentFiles((prev) => {
        const newArray = [...prev, ...files];
        const unique = [];
        const seen = new Set();
        newArray.forEach((f) => {
          if (!seen.has(f.name)) {
            seen.add(f.name);
            unique.push(f);
          }
        });
        return unique;
      });
      setLocalSelectedDocs((prev) => {
        const next = [...prev];
        files.forEach((f) => {
          if (!next.some((p) => p.name === f.name)) {
            next.push(f);
          }
        });
        return next;
      });
    }
    e.target.value = null;
  };

  const handleDocumentToggle = (file) => {
    setLocalSelectedDocs((prev) => {
      if (prev.some((p) => p.name === file.name)) {
        return prev.filter((p) => p.name !== file.name);
      }
      return [...prev, file];
    });
  };

  const handleDocumentToggleAll = (checked) => {
    if (checked) {
      setLocalSelectedDocs([...stagedDocumentFiles]);
    } else {
      setLocalSelectedDocs([]);
    }
  };

  const handleDocumentOk = () => {
    const docColKey = Object.keys(rowMappings || {}).find(
      (k) => rowMappings[k] === 'DOCUMENTS',
    );

    let docColName = 'Documents';
    if (docColKey) {
      const cr = customRows?.find((r) => r.id === docColKey);
      if (cr && cr.name !== '-') docColName = cr.name;
      else if (docColKey.startsWith('prop_')) docColName = docColKey.substring(5);
    }

    setParsedData((prev) =>
      prev.map((row) => {
        if (row.id === documentMatchRowId) {
          const actualDocKey =
            Object.keys(row).find(
              (k) => normalizeString(k) === normalizeString(docColName),
            ) || docColName;

          return {
            ...row,
            [actualDocKey]:
              localSelectedDocs && localSelectedDocs.length > 0
                ? localSelectedDocs.length === 1
                  ? localSelectedDocs[0].name
                  : `${localSelectedDocs.length} documents`
                : '',
            documents: localSelectedDocs || [],
            _manuallyAssigned: {
              ...(row._manuallyAssigned || {}),
              [actualDocKey]: localSelectedDocs && localSelectedDocs.length > 0,
            },
          };
        }
        return row;
      }),
    );
    setDocumentMatchRowId(null);
  };

  const handleDocumentCancel = () => {
    setDocumentMatchRowId(null);
  };

  const initDocumentMatch = (row) => {
    setDocumentMatchRowId(row.id);
    if (row.documents) {
      setLocalSelectedDocs(row.documents);
    } else {
      setLocalSelectedDocs([]);
    }
  };

  const resetDocuments = () => {
    setDocumentMatchRowId(null);
    setStagedDocumentFiles([]);
    setLocalSelectedDocs([]);
  };

  return {
    documentMatchRowId,
    stagedDocumentFiles,
    localSelectedDocs,
    initDocumentMatch,
    handleDocumentFileChange,
    handleDocumentToggle,
    handleDocumentToggleAll,
    handleDocumentOk,
    handleDocumentCancel,
    setStagedDocumentFiles,
    setDocumentMatchRowId,
    resetDocuments,
  };
};
